import React, { useState, useEffect, useRef } from 'react';

const RhythmGame = ({ gameData, onComplete }) => {
    const [currentPatternIndex, setCurrentPatternIndex] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isListening, setIsListening] = useState(false);
    const [userTaps, setUserTaps] = useState([]);
    const [feedback, setFeedback] = useState(null); 
    const [score, setScore] = useState(0);
    const [activeBeat, setActiveBeat] = useState(false);
    const [countdown, setCountdown] = useState(null);

    const currentPattern = gameData?.patterns?.[currentPatternIndex];
    const startTimeRef = useRef(null);
    const timeoutRefs = useRef([]);
    const audioCtxRef = useRef(null);

    useEffect(() => {
        return () => {
            timeoutRefs.current.forEach(clearTimeout);
            if (audioCtxRef.current) audioCtxRef.current.close();
        };
    }, []);

    const playClickSound = (frequency = 800) => {
        try {
            if (!audioCtxRef.current) {
                audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
            }
            const ctx = audioCtxRef.current;
            if (ctx.state === 'suspended') ctx.resume();
            
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.type = 'sine';
            osc.frequency.setValueAtTime(frequency, ctx.currentTime);
            gain.gain.setValueAtTime(0.4, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.00001, ctx.currentTime + 0.15);
            osc.start();
            osc.stop(ctx.currentTime + 0.15);
        } catch (e) {
            console.error("Audio error:", e);
        }
    };

    const getExpectedTimes = () => {
        if (!currentPattern) return [];
        const sequence = currentPattern.pattern.split('');
        const beatDuration = 60000 / (currentPattern.bpm || 80);
        let currentTime = 0;
        const expectedTimes = [];

        sequence.forEach((char) => {
            if (char === '●') {
                expectedTimes.push(currentTime);
            }
            currentTime += beatDuration;
        });
        return expectedTimes;
    };

    const playPattern = () => {
        if (isPlaying || isListening || feedback) return;

        setIsPlaying(true);
        setUserTaps([]);
        
        const expectedTimes = getExpectedTimes();
        
        expectedTimes.forEach((time, index) => {
            const tId = setTimeout(() => {
                setActiveBeat(true);
                playClickSound(900);
                if (navigator.vibrate) navigator.vibrate(20);
                setTimeout(() => setActiveBeat(false), 150);
            }, time);
            timeoutRefs.current.push(tId);
        });

        const totalDuration = (currentPattern.pattern.length * (60000 / (currentPattern.bpm || 80)));
        const tId = setTimeout(() => {
            setIsPlaying(false);
            startUserTurn();
        }, totalDuration + 500);
        timeoutRefs.current.push(tId);
    };

    const startUserTurn = () => {
        let count = 3;
        setCountdown(count);
        if (navigator.vibrate) navigator.vibrate(50);
        
        const countInterval = setInterval(() => {
            count -= 1;
            if (count > 0) {
                setCountdown(count);
                playClickSound(400);
                if (navigator.vibrate) navigator.vibrate(50);
            } else {
                clearInterval(countInterval);
                setCountdown('PRADEDAM!');
                playClickSound(1200);
                if (navigator.vibrate) navigator.vibrate(100);
                setTimeout(() => setCountdown(null), 500);
                setIsListening(true);
                startTimeRef.current = Date.now();
            }
        }, 600);
    };

    const handleTap = (e) => {
        if (!isListening) return;
        if (e) {
            if (e.type === 'touchstart') {
                e.preventDefault();
            }
        }

        playClickSound(800);
        setActiveBeat(true);
        setTimeout(() => setActiveBeat(false), 100);
        if (navigator.vibrate) navigator.vibrate(40);

        const tapTime = Date.now() - startTimeRef.current;
        const newTaps = [...userTaps, tapTime];
        setUserTaps(newTaps);

        const expectedTimes = getExpectedTimes();
        if (newTaps.length === expectedTimes.length) {
            validateRhythm(newTaps, expectedTimes);
        }
    };

    const validateRhythm = (userTimes, expectedTimes) => {
        setIsListening(false);
        const tolerance = 400; // Tolerance for children
        let misses = 0;

        // Normalize user taps so the first tap is always at time 0
        const firstTap = userTimes[0] || 0;
        const normalizedUserTimes = userTimes.map(t => t - firstTap);

        normalizedUserTimes.forEach((time, i) => {
            const diff = Math.abs(time - expectedTimes[i]);
            if (diff > tolerance) misses++;
        });

        if (misses === 0) {
            const addedScore = score + (gameData.points_per_pattern || 25);
            setScore(addedScore);
            setFeedback({ status: 'correct', message: 'TOBULAS RITMAS! 🌟' });
            if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
            
            setTimeout(() => {
                setFeedback(null);
                if (currentPatternIndex < gameData.patterns.length - 1) {
                    setCurrentPatternIndex(prev => prev + 1);
                    setUserTaps([]);
                } else {
                    if (navigator.vibrate) navigator.vibrate([200, 100, 200, 100, 300]);
                    onComplete?.(addedScore);
                }
            }, 4500);
        } else {
            setFeedback({ status: 'wrong', message: misses > 1 ? 'BANDYK DAR KARTĄ! 👂' : 'BEVEIK PAVYKO!' });
            if (navigator.vibrate) navigator.vibrate([50, 50, 50]);
            setTimeout(() => {
                setFeedback(null);
                setUserTaps([]);
            }, 3000);
        }
    };

    if (!gameData || !gameData.patterns) {
        return (
            <div className="premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-12 text-center border-indigo-100 animate-pulse bg-white">
                <div className="text-lg md:text-4xl md:text-[100px] mb-8">🥁</div>
                <h3 className="text-base md:text-3xl font-black text-indigo-900 tracking-tighter uppercase">Derinami būgnai...</h3>
            </div>
        );
    }

    const progress = (currentPatternIndex / gameData.patterns.length) * 100;

    return (
        <div className="space-y-3 md:space-y-8 animate-slide-up select-none">
            {/* Header / Rhythm HUD */}
            <div className="premium-glass rounded-lg md:rounded-[2.5rem] p-3 md:p-8 border-white/40 shadow-2xl relative overflow-hidden bg-indigo-50">
                <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/5 rounded-full blur-[80px] pointer-events-none"></div>
                <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
                    <div className="flex items-center gap-2 md:p-6">
                        <div className="w-20 h-20 bg-indigo-100 rounded-3xl flex items-center justify-center text-lg md:text-4xl shadow-inner border-2 border-indigo-200 animate-float">
                            🥁
                        </div>
                        <div>
                            <h3 className="text-lg md:text-4xl font-black text-slate-800 tracking-tighter leading-tight uppercase">
                                Ritmo <span className="text-indigo-600">Meistras</span>
                            </h3>
                            <p className="text-slate-500 font-bold italic font-serif">
                                Įsiklausyk į melodiją ir atkartok ją tiksliai!
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 md:p-6">
                        <div className="bg-white/50 px-8 py-4 rounded-[2rem] border-2 border-white shadow-inner">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-2">Energija</p>
                            <p className="text-base md:text-3xl font-black text-indigo-600 leading-none">⭐ {score}</p>
                        </div>
                        <div className="bg-white/50 px-8 py-4 rounded-[2rem] border-2 border-white shadow-inner text-center min-w-[120px]">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-2">Ritmas</p>
                            <p className="text-base md:text-3xl font-black text-slate-800 leading-none">{currentPatternIndex + 1}/{gameData.patterns.length}</p>
                        </div>
                    </div>
                </div>

                <div className="mt-8 w-full bg-slate-200/50 rounded-full h-4 p-1 shadow-inner overflow-hidden border-2 border-slate-300">
                    <div 
                        className="h-full bg-gradient-to-r from-indigo-400 via-purple-500 to-rose-600 rounded-full transition-all duration-1000 shadow-[0_0_20px_rgba(99,102,241,0.4)]"
                        style={{ width: `${progress}%` }}
                    />
                </div>
            </div>

            {/* Performance Stage Interaction Area */}
            <div className="premium-glass rounded-2xl md:rounded-[4rem] p-4 md:p-10 lg:p-4 md:p-16 shadow-2xl border-white/60 bg-white/40 backdrop-blur-xl relative overflow-hidden flex flex-col items-center min-h-[50vh] justify-center text-center">
                <div className="absolute top-0 right-0 p-4 md:p-12 text-2xl md:text-6xl md:text-[200px] opacity-[0.03] select-none pointer-events-none rotate-12">🥁</div>
                <div className="absolute bottom-0 left-0 p-4 md:p-12 text-2xl md:text-6xl md:text-[200px] opacity-[0.03] select-none pointer-events-none -rotate-12 text-indigo-600">🎹</div>
                
                {countdown ? (
                    <div className="text-2xl md:text-6xl md:text-[180px] font-black text-indigo-600 animate-pop-in drop-shadow-[0_20px_40px_rgba(99,102,241,0.4)] z-50 italic">
                        {countdown}
                    </div>
                ) : (
                    <div className="w-full max-w-5xl space-y-4 md:space-y-12 relative z-10">
                        {currentPattern.description && (
                            <div className="bg-white/80 backdrop-blur-md px-10 py-5 rounded-3xl border-2 border-indigo-100 shadow-lg inline-block animate-pop-in">
                                <p className="text-2xl font-black text-indigo-800 italic">💡 {currentPattern.description}</p>
                            </div>
                        )}
                        {/* High-End Visual Rhythm Scrubber */}
                        <div className="flex justify-center items-center gap-2 md:p-6 flex-wrap bg-slate-900/10 p-4 md:p-12 rounded-2xl md:rounded-[4rem] border-4 border-white shadow-inner">
                            {currentPattern.pattern.split('').map((char, i) => {
                                const isBeat = char === '●';
                                // In user turn, show the beat or highlight the next one
                                const isUserCurrent = isListening && i === userTaps.length;
                                const isUserHit = isListening && i < userTaps.length && isBeat;
                                
                                return (
                                    <div key={i} className={`
                                        w-16 h-16 md:w-20 md:h-20 rounded-3xl border-4 transition-all duration-300 flex items-center justify-center relative group
                                        ${isBeat ? 'bg-white border-white shadow-xl' : 'bg-slate-200 border-slate-300 opacity-20'}
                                        ${activeBeat && i === (isListening ? userTaps.length - 1 : i) ? 'scale-125 !bg-yellow-400 !border-yellow-200 shadow-[0_0_30px_rgba(250,204,21,0.6)]' : ''}
                                        ${isUserCurrent ? 'ring-8 ring-indigo-400/30 scale-110' : ''}
                                        ${isUserHit ? 'bg-indigo-500 border-indigo-400' : ''}
                                    `}>
                                        {isBeat && <div className={`w-6 h-6 rounded-lg transition-all ${isUserHit ? 'bg-white' : 'bg-indigo-600/20'}`} />}
                                        {isUserCurrent && <div className="absolute -top-4 md:p-12 text-2xl animate-bounce">👇</div>}
                                    </div>
                                );
                            })}
                        </div>

                        {/* Interactive Quantum Drum Pad */}
                        <div className="relative group flex flex-col items-center gap-4 md:p-10">
                            <div className={`
                                absolute inset-0 bg-indigo-500/20 rounded-full blur-[100px] transition-all duration-700
                                ${isListening ? 'opacity-100 scale-110' : 'opacity-0 scale-90'}
                            `} />
                            
                            <div className="relative">
                                <button
                                    onMouseDown={(e) => { if (!isPlaying && !isListening && !feedback) playPattern(); else handleTap(e); }}
                                    onTouchStart={(e) => { if (!isPlaying && !isListening && !feedback) playPattern(); else handleTap(e); }}
                                    className={`
                                        w-72 h-72 md:w-96 md:h-96 rounded-3xl md:rounded-[5rem] flex flex-col items-center justify-center gap-2 md:p-6 shadow-[0_30px_100px_rgba(0,0,0,0.3)] transition-all duration-150 relative z-10 border-b-[20px] border-indigo-900/20 transform-gpu
                                        ${activeBeat ? 'scale-90 bg-indigo-700 border-b-0 translate-y-4 text-white shadow-none' : 'bg-white text-indigo-500 hover:scale-105 active:scale-95'}
                                        ${isListening ? 'ring-[20px] ring-indigo-400/20 animate-pulse-subtle' : ''}
                                        ${isPlaying ? 'cursor-default pointer-events-none opacity-40 scale-95' : 'cursor-pointer'}
                                    `}
                                >
                                    <div className="text-[140px] drop-shadow-2xl transition-transform duration-500 group-hover:rotate-12">
                                        {isPlaying ? '👂' : isListening ? '🥁' : '▶️'}
                                    </div>
                                    <div className="space-y-2">
                                        <span className="text-[10px] font-black uppercase tracking-[0.6em] block opacity-40">
                                            {isPlaying ? 'KLAUSYKIS...' : isListening ? 'ATKARTOK DABAR!' : 'SPAUSK PRADĖTI'}
                                        </span>
                                        <div className="h-1 w-20 bg-indigo-100 mx-auto rounded-full" />
                                    </div>
                                </button>
                                
                                {/* Visual Ripple Effect on Tap */}
                                {activeBeat && (
                                    <div className="absolute inset-0 bg-white/20 rounded-3xl md:rounded-[5rem] animate-ping pointer-events-none" />
                                )}
                            </div>

                            {!isPlaying && !isListening && !feedback && (
                                <div className="animate-bounce pt-6">
                                    <p className="text-indigo-400 font-black text-sm uppercase tracking-widest bg-indigo-50 px-10 py-4 rounded-full border-2 border-indigo-100 shadow-xl">Paliesk būgną, kad išgirstum ritmą!</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Tactical Full-Screen Rhythm Controller */}
                {isListening && (
                    <div 
                        onMouseDown={(e) => handleTap(e)}
                        onTouchStart={(e) => handleTap(e)}
                        className="absolute inset-0 z-50 cursor-pointer active:bg-indigo-500/5 transition-colors touch-none"
                    />
                )}
            </div>

            {/* Performance Results Overlay */}
            {feedback && (
                <div className="fixed inset-0 flex items-center justify-center z-[1000] p-2 md:p-6">
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-xl animate-fade-in" />
                    <div className={`
                        max-w-2xl w-full p-4 md:p-16 rounded-3xl md:rounded-[5rem] bg-white shadow-[0_40px_100px_rgba(0,0,0,0.5)] relative animate-pop-in border-[12px]
                        ${feedback.status === 'correct' ? 'border-emerald-500' : 'border-rose-500'}
                    `}>
                        <div className="absolute -top-4 md:p-16 left-1/2 -translate-x-1/2 w-32 h-32 bg-white rounded-full flex items-center justify-center text-7xl shadow-2xl border-8 border-inherit animate-bounce">
                            {feedback.status === 'correct' ? '🌟' : '👂'}
                        </div>
                        
                        <div className="text-center space-y-4 md:space-y-10 pt-10">
                            <h2 className={`text-2xl md:text-6xl font-black uppercase tracking-tighter leading-none ${feedback.status === 'correct' ? 'text-emerald-600' : 'text-rose-600'}`}>
                                {feedback.message}
                            </h2>

                            <div className={`p-4 md:p-12 rounded-2xl md:rounded-[4rem] border-4 text-left italic font-serif leading-relaxed ${feedback.status === 'correct' ? 'bg-emerald-50 border-emerald-100' : 'bg-rose-50 border-rose-100'}`}>
                                <p className="text-base md:text-3xl font-bold text-slate-700 text-center leading-relaxed italic">
                                    {feedback.status === 'correct' 
                                        ? "Nuostabu! Tavo vidinis metronomas veikia nepriekaištingai. Gamta džiaugiasi tavo tobulu ritmu!" 
                                        : "Muzika reikalauja kantrybės. Įsiklausyk į būgno širdies dūžius ir bandyk juos atkartoti dar kartą."}
                                </p>
                            </div>

                            <div className="flex items-center justify-center gap-4">
                                <div className="flex gap-2">
                                    <div className="w-4 h-4 bg-slate-200 rounded-full animate-bounce" />
                                    <div className="w-4 h-4 bg-slate-200 rounded-full animate-bounce [animation-delay:0.2s]" />
                                    <div className="w-4 h-4 bg-slate-200 rounded-full animate-bounce [animation-delay:0.4s]" />
                                </div>
                                <span className="text-sm font-black text-slate-300 uppercase tracking-[0.3em] ml-2">Derinamas sekantis taktas...</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RhythmGame;
